import React, { useContext, useEffect, useState, useRef } from "react";
import { AppContext } from "../../utils/Context/AppContext";
import Back from "../../assets/back.svg";
import { Link, useNavigate } from "react-router-dom";
import AudioPlayer from "./AudioPlayer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import html2pdf from "html2pdf.js";
import LOGO from "../../assets/pdf_Logo.png";
import { faTimes, faCheck, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { HiOutlineDownload } from "react-icons/hi";
import { saveNewUpdatedSummary } from "../../utils/services/api";
import ModelSvg from "../../assets/Model.svg";
import { FaRegEdit } from "react-icons/fa";

const Summarized = () => {
  const editorRef = useRef(null);

  const { dispatch, appData } = useContext(AppContext);
  const [isEdit, setIsEdit] = useState(false);

  const [saveModal, setSaveModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const navigate = useNavigate();

  const loginClient = localStorage.getItem("userId");

  // const staticContent = appData.summary; // using from context
  // const staticContent = `<!DOCTYPE html><html lang=\"en\"><head><meta charset=\"UTF-8\"><style>    @page {        size: A4;        margin: 0.5in;    }    body {        font-family: 'Aptos', Arial, sans-serif;        font-size: 12px;        line-height: 1.6;        margin: 0.5;        background: #fff;        color: #222;    }    h2 {        font-family: 'Aptos', Arial, sans-serif;        font-size: 14px;        font-weight: bold;        color: #4c94d8;        margin-top: 1.2em;        margin-bottom: 0.5em;    }    p, ul, li, div, strong, em, code {        font-family: 'Aptos', Arial, sans-serif;        font-size: 12px;        margin: 0.5;    }    ul {        margin-left: 1.2em;        margin-bottom: 1em;    }    .note {        background: #eaf7ff;        border-left: 4px solid #4c94d8;        padding: 0.8em 1em;        margin: 1em 0;        font-style: italic;    }    code {        background: #eee;        padding: 2px 6px;        border-radius: 3px;        font-family: 'Aptos Mono', 'Consolas', monospace;        font-size: 12px;    }    .highlight {        background: #eaf7ff;        padding: 0.2em 0.4em;        border-radius: 2px;    }</style></head><body><h2>Body-Protected and Cardiac-Protected Installations in Medical Facilities</h2><p>The requirements for body-protected and cardiac-protected installations in medical facilities are determined by the entity in control of the medical area. The primary reference for these installations is <strong>AS3003</strong>, which details the design and installation requirements for patient areas.</p><h2>Understanding AS3003 and Patient Areas</h2><p>AS3003, titled <em>Patient Areas</em>, provides specific design requirements for circuitry in patient areas. It is essential to thoroughly understand this standard before undertaking any installation work. The standard is available on MKB and should be consulted as the first step in the design process.</p><p>Key points from AS3003 include:</p><ul>    <li>Patient areas must have dedicated circuits.</li>    <li>Additional earthing and higher performance requirements apply.</li>    <li>Earth continuity results must not exceed <strong>0.1 ohm</strong>.</li>    <li>Cables within 50mm of a building surface and not RCD protected must have mechanical protection, typically steel conduit.</li>    <li>Earth conductor sizing and maximum cable lengths are specified to ensure compliance with resistance requirements.</li></ul><h2>Design Parameters and Cable Selection</h2><p>The design of body-protected circuits must consider the resistance per metre of the conductor, as outlined in <strong>Table 34 of AS3008</strong>. This table provides the resistance of conductors at various operating temperatures per kilometre. Calculations should be made as follows:</p><ul>    <li>Divide the resistance per kilometre by 1,000 to obtain resistance per metre.</li>    <li>Multiply by the length of the cable to determine total resistance.</li></ul><p>For example, a 6mm<sup>2</sup> conductor 30 metres long will have a resistance of approximately 2.5 ohms, which may cause a continuity test to fail. These parameters must be considered before quoting or commencing work.</p><h2>Scope and Areas Requiring Body Protection</h2><p>AS3003 provides a comprehensive list of areas where body-protected installations are required, including treatment rooms and any space where medical equipment comes into contact with the human body. The standard also specifies that typically 1.5 metres around any body-protected area must also comply.</p><h2>Mechanical Protection and RCD Requirements</h2><p>If a cable is not RCD protected, it must be mechanically protected, usually with steel conduit. The installation of steel conduit is more costly than standard cable installation. The location of RCDs is also critical:</p><ul>    <li>RCDs must be located at the switchboard from which the circuit originates.</li>    <li>For body-protected areas, RCD protection must be provided in the room defined as a body-protected area.</li></ul><p>Methods of protection are detailed in <strong>AS3000</strong> clauses 263233, 2942, and 3944. Clause 3942 covers cables likely to be disturbed, while 3944 outlines methods of protection, including steel conduit (part A) and armoured or screened cable (part B).</p><h2>Practical Installation Considerations</h2><p>In ceiling spaces that are not accessible, or where cables are on a cable tray, mechanical protection may not be required until the cable penetrates the wall. In such cases, a steel conduit dropper is used to comply with the standard. The current interpretation is that mechanical protection is only required within 50mm of a building surface, typically the wall.</p><p>The industry is awaiting clarification in the next revision of the standard, which may require steel conduit in all wall drops, regardless of wall thickness.</p><h2>Cost Implications and Quoting</h2><p>The cost of installing body-protected circuits is significantly higher than standard circuits due to the additional requirements. For example, a single LPD circuit less than 30 metres from a switchboard in a hospital setting is typically priced at around <span class=\"highlight\">[redacted]</span>. In medical centres, the cost may be lower if catenary wire is used instead of cable tray.</p><p>It is important to differentiate between the cost of body-protected circuits and standard GPO circuits when quoting. The installation practices and materials required are substantially different.</p><h2>RCD Types and Load Considerations</h2><p>Type 1 RCDs (10mA) are now commonly used in body-protected areas. Both type 1 and type 2 RCDs are built to the same standard, which allows operation between 50% and 100% of the rated trip current. Electronic equipment with switch mode or pulse width modulated power supplies can contribute to leakage current, potentially causing nuisance tripping.</p><p>When designing circuits, allow for at least 1mA of leakage current per device with a switch mode power supply. Careful consideration must be given to the number of outlets protected by a single 10mA RCD to avoid nuisance tripping.</p><h2>Defining Spaces and Consultation with Clients</h2><p>AS3003 requires the responsible entity to define the use of each space and the equipment to be installed. The electrician must design the installation based on this information. It is essential to consult with the client to determine the intended use of each room and the medical equipment to be used.</p><p>For example, a consulting room with only a blood pressure cuff may not require body protection, while a room with an ultrasound machine will.</p><h2>Labelling and Colour Coding of Outlets</h2><p>Body-protected circuits are typically identified with red GPOs, while general-purpose and cleaners' outlets are labelled accordingly. The standard requires permanent, indelible labels with lettering at least 6mm high. The wording must include phrases such as <strong>Cleaners GPO only</strong>.</p><p>While the standard does not mandate specific colours, many facilities adopt colour coding for visual clarity:</p><ul>    <li>Red for life-protected device circuitry</li>    <li>Green for general-purpose outlets</li>    <li>Beige for cleaners' outlets</li></ul><p>When quoting, specify the colour coding and labelling approach to justify any additional costs. If the client prefers not to use colour coding, labels are still required.</p><h2>Consultation and Tendering Process</h2><p>During the tendering process, offer options for labelling only or labelling with colour coding for enhanced safety. This approach demonstrates professionalism and a commitment to electrical safety, which can be a deciding factor for clients.</p><p>It is important to clarify all inclusions and exclusions in the quote, especially regarding outlet colours and labelling, to avoid misunderstandings and ensure compliance with client expectations and standards.</p><h2>Industry Context and Ongoing Developments</h2><p>Recent changes and interpretations of the standards have had significant impacts on healthcare projects, particularly in Queensland. Disputes over the application of mechanical protection requirements have led to increased costs and industry-wide discussions. The outcome of ongoing standard reviews will further clarify these requirements.</p><p>The healthcare sector continues to see substantial investment in new facilities, making compliance with current and emerging standards critical for all contractors involved in medical installations.</p><div class=\"note\">    <strong>Note:</strong> Always refer to the latest version of AS3003 and AS3000 for detailed requirements and consult with the responsible entity to ensure all installations are compliant and fit for purpose.</div></body></html>`;
  const [summary, setSummary] = useState(appData.summary);

  const handleEditClick = () => setIsEdit(true);

  const handleSaveClick = () => {
    if (editorRef.current) {
      setSummary(editorRef.current.innerHTML); // Save the HTML content
      setIsEdit(false);
    }
  };

  const handleCancelClick = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = summary; // Revert to last saved content
      setIsEdit(false);
    }
  };

  const handleFileNameChange = (event) => {
    dispatch({ type: "AUDIO_FILE_NAME", payload: event.target.value });
  };

  const saveUpdatedSummary = async () => {
    setIsLoading(true);
    try {
      const isFileSaved = await saveNewUpdatedSummary(
        summary,
        appData.fileName
      );
      setDialogMessage(
        isFileSaved ? "File saved successfully!" : "Failed to save file."
      );
      dispatch({ type: "SUCCESS_MODEL_OPEN", payload: true });
    } catch (error) {
      console.error("Error while saving summary:", error);
      setDialogMessage("An error occurred while saving the file.");
      dispatch({ type: "SUCCESS_MODEL_OPEN", payload: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () =>
    dispatch({ type: "SUCCESS_MODEL_FALSE", payload: false });

  const downloadPDF = () => {
    const element = document.createElement("div");
    element.innerHTML = `
    <div style="padding: 10px; font-family: Helvetica, Arial, sans-serif;">
      <img src="${LOGO}" alt="Logo" style="width: 60px; margin-bottom: 20px;" />
      <div style="font-size: 14px; text-align: justify; white-space: pre-wrap;">
        ${summary}
      </div>
    </div>
  `;

    const options = {
      margin: 0.5,
      filename: `${appData.fileName || "summary"}.pdf`,
      html2canvas: { scale: 2, scrollY: -window.scrollY },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    html2pdf().set(options).from(element).save();
  };

  useEffect(() => {
    if (!appData.audioFile) {
      navigate("/upload-audio");
    }
  }, [appData.summary, navigate]);

  return (
    <div className="h-full w-full flex flex-col gap-[2px]">
      {/* Header */}
      <div className="h-[35px] w-full flex items-center gap-2 py-2 px-4 flex-shrink-0">
        <Link to="/upload-audio" className="flex gap-2 items-center">
          <img src={Back} alt="Back" className="w-7 h-7" />
          <span className="text-[#242424] font-medium text-[14px]">Back</span>
        </Link>
      </div>

      <div className="h-[calc(100%-35px)] w-full flex flex-col gap-2 p-2 overflow-hidden">
        {/* Audio Player */}
        <div className="flex-shrink-0 w-full bg-white rounded-md p-2">
          {appData.audioFile ? (
            <AudioPlayer audioFile={appData.audioFile} />
          ) : (
            <p>No audio file to play</p>
          )}
        </div>

        {/* Summary Section */}
        <div className="bg-white flex-1 w-full flex flex-col overflow-hidden p-2">
          <div
            id="summary-container"
            className="font-custom h-full text-sm font-normal leading-relaxed bg-white rounded p-2 m-auto overflow-auto w-full md:w-[90%] lg:w-[80%] 2xl:w-[60%] shadow-md"
          >
            {isEdit ? (
              <div
                ref={editorRef}
                contentEditable={isEdit}
                suppressContentEditableWarning={true} // prevent React warning
                className="h-full overflow-auto p-2 border rounded-md outline-none"
                style={{
                  lineHeight: "1.6",
                  fontFamily: "'Aptos', Arial, sans-serif",
                }}
                dangerouslySetInnerHTML={{ __html: summary }}
              />
            ) : (
              <div
                className="h-full overflow-auto p-2 border rounded-md outline-none"
                dangerouslySetInnerHTML={{ __html: summary }}
                style={{ lineHeight: "1.6" }}
              />
            )}
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex gap-3 px-2 pt-2 bg-slate-100 border-t border-gray-300 items-center">
          {!isEdit ? (
            <>
              {/* Edit Button */}
              <div
                onClick={handleEditClick}
                className="flex items-center justify-center p-2 border border-gray-300 rounded-md hover:bg-slate-400 text-[#616161] hover:text-white cursor-pointer md:hidden"
              >
                <FaRegEdit size={20} />
              </div>
              <button
                onClick={handleEditClick}
                className="hidden md:flex gap-2 text-[14px] items-center border hover:bg-slate-400 text-[#616161] hover:text-white  border-gray-300  py-1.5 px-3 rounded-md font-medium"
              >
                Edit
                <span>
                  <FaRegEdit size={18} />
                </span>{" "}
              </button>

              {/* Download PDF Button */}
              <div
                onClick={downloadPDF}
                className="flex items-center justify-center p-1 border border-gray-300 rounded-md hover:bg-slate-400 text-[#616161] hover:text-white cursor-pointer md:hidden"
              >
                {" "}
                <HiOutlineDownload size={22} />{" "}
              </div>
              {/* Button - visible on medium screens and above */}
              <button
                onClick={downloadPDF}
                className="hidden md:flex gap-2 text-[15px] items-center bg-[#01AEEF] hover:bg-[#018ec2] text-white py-1.5 px-3 rounded-md font-medium"
              >
                Download
                <span>
                  <HiOutlineDownload size={18} />
                </span>
              </button>
            </>
          ) : (
            <>
              <FontAwesomeIcon
                icon={faCheck}
                onClick={handleSaveClick}
                className="cursor-pointer w-5 h-auto text-[#616161]"
                title="Save"
              />
              <FontAwesomeIcon
                icon={faTimes}
                onClick={handleCancelClick}
                className="cursor-pointer w-5 h-auto text-[#616161]"
                title="Cancel"
              />
            </>
          )}

          <button
            className={`ml-auto text-[14px] text-center px-6 py-1.5 rounded-md text-white ${
              isEdit ? "bg-slate-500 cursor-not-allowed" : "bg-[#01AEEF]"
            }`}
            onClick={() => setSaveModal(!saveModal)}
            disabled={isLoading || isEdit}
          >
            {isLoading ? (
              <FontAwesomeIcon icon={faSpinner} spin className="w-4 h-4" />
            ) : (
              <>Save</>
            )}
          </button>
        </div>
      </div>

      {/* Dialog */}
      {appData?.successModal && (
        <div className="fixed inset-0 top-0 bg-gray-400 bg-opacity-50 flex justify-center items-center z-[100] p-4">
          <div className="bg-white p-6 rounded-lg shadow-lg w-auto text-center relative">
            <img src={ModelSvg} alt="Model" className="mx-auto mb-4" />
            <button
              onClick={handleDialogClose}
              className="px-4 py-2 bg-[#01AEEF] text-white rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Save Modal */}
      {saveModal && (
        <div className="fixed inset-0 top-0 bg-gray-400 bg-opacity-50 flex justify-center items-center z-[100] p-4">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg relative flex flex-col gap-3">
            <div>
              Please confirm the current file name or change it before saving.
            </div>
            <input
              className="border border-[#CBD1D8] rounded-md h-8 w-full outline-none p-2 text-[#797878] text-sm"
              value={appData.fileName}
              onChange={handleFileNameChange}
            />
            <div className="flex justify-between w-full gap-3 mt-2">
              <button
                onClick={() => setSaveModal(false)}
                className="w-full border border-[#01AEEF] text-[#01AEEF] rounded-[40px] py-2"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setSaveModal(false);
                  saveUpdatedSummary();
                }}
                className="w-full bg-[#01AEEF] text-white rounded-[40px] py-2"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Summarized;
